import { useEffect, useRef } from 'react';

/**
 * COPILOT MODE: the transport and the discretionary book, on six keys.
 *
 * <h2>Why keys at all, when every one of these is already a button</h2>
 *
 * The thing a copilot is being asked to do is decide WHILE WATCHING. A mouse trip to a transport
 * at the bottom of the chart takes the eyes off the bar the decision is about — and at the 50ms
 * end of the speed slider the bar is gone before the pointer arrives. A key press costs no gaze.
 *
 * <h2>THE RESOLVER IS PURE, and that is where the tests are</h2>
 *
 * {@link resolveHotkey} takes an event-shaped object and returns an action or null. Everything
 * that is actually worth asserting here is a decision about whether a key press counts —
 * typing in a field, a modifier held, an auto-repeat — and none of it needs a DOM or a chart. The
 * hook is then four lines of listener plumbing over it.
 *
 * <h2>Three refusals, each of which is a real bug if missed</h2>
 *
 * <ul>
 *   <li><b>Never while typing.</b> The symbol box, the speed field and every dialog input are one
 *       tab away. A reader typing "SPY" would FLATTEN, SELL and PLAY — a key handler that fires
 *       over a text field is how a chart feature deletes somebody's position.</li>
 *   <li><b>Never with ctrl / meta / alt.</b> Ctrl+S is save and Cmd+ArrowLeft is browser-back.
 *       Shift IS allowed, because the brief names 'B' and 'S' in upper case and a reader who
 *       holds shift means the letter.</li>
 *   <li><b>No auto-repeat on anything but stepping.</b> Holding ArrowRight to scrub is exactly
 *       right; holding the spacebar would toggle play and pause dozens of times a second, and
 *       holding 'B' would open and reverse a position on every repeat. Stepping is idempotent in
 *       spirit — one more bar — and the other four are not.</li>
 * </ul>
 */

/** What each key does. Lower-cased, so shift does not change the meaning. */
export const HOTKEYS = Object.freeze({
  ' ': 'TOGGLE',
  arrowright: 'STEP',
  arrowleft: 'BACK',
  b: 'BUY',
  s: 'SELL',
  x: 'FLAT',
});

/** The two that may auto-repeat: holding an arrow to scrub is the intended gesture. */
export const REPEATABLE = Object.freeze(['STEP', 'BACK']);

/** Whether the event came from somewhere a keystroke means a character. */
export function isTypingTarget(target) {
  if (!target) return false;
  if (target.isContentEditable) return true;
  const tag = typeof target.tagName === 'string' ? target.tagName.toUpperCase() : '';
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * The action a key press means, or null.
 *
 * @param event anything with `key`, the modifier flags, `repeat` and `target`
 */
export function resolveHotkey(event) {
  if (!event || typeof event.key !== 'string') return null;
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (isTypingTarget(event.target)) return null;

  // `' '` is the spacebar's own key value; `Spacebar` is the legacy IE/Edge spelling and costs one
  // comparison to keep working.
  const key = event.key === 'Spacebar' ? ' ' : event.key.toLowerCase();
  const action = HOTKEYS[key];
  if (!action) return null;
  if (event.repeat && !REPEATABLE.includes(action)) return null;
  return action;
}

/** Keys whose browser default has to be suppressed: the page would scroll. */
export const SCROLLING_KEYS = Object.freeze([' ', 'spacebar', 'arrowright', 'arrowleft']);

/**
 * Binds the six keys for as long as `enabled`.
 *
 * <p>The handlers are read through a REF, so a parent that rebuilds its callbacks every bar — and
 * this one does, because the current bar is a dependency of a human action — does not tear down
 * and re-add a window listener on every tick of the clock. The listener is added once per
 * enable/disable, which is also what makes the cleanup trivially correct.
 *
 * @param enabled  false unbinds entirely, so the keys do nothing outside a replay
 * @param handlers {{onToggle, onStep, onBack, onHumanAction}} — `onHumanAction` is called with
 *                 'BUY' | 'SELL' | 'FLAT', the same vocabulary the HUD buttons use
 */
export default function useReplayHotkeys(enabled, handlers) {
  const ref = useRef(handlers);
  useEffect(() => { ref.current = handlers; }, [handlers]);

  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (event) => {
      const action = resolveHotkey(event);
      if (!action) return;

      // `event.key` is a string by now — `resolveHotkey` returned null otherwise — so there is no
      // fallback here. A defensive ternary would be an unreachable branch, and an unreachable
      // branch is a line nobody can ever show to be right.
      if (SCROLLING_KEYS.includes(event.key.toLowerCase())) event.preventDefault();

      // `|| {}` because `handlers` is genuinely optional: a caller may bind the keys before it
      // has anything to wire them to, and a replay that threw on a key press would be worse than
      // one where a key does nothing yet.
      const h = ref.current || {};
      if (action === 'TOGGLE') h.onToggle?.();
      else if (action === 'STEP') h.onStep?.();
      else if (action === 'BACK') h.onBack?.();
      else h.onHumanAction?.(action);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
